import React, {
  KeyboardEventHandler,
  MouseEventHandler,
  useContext,
  useEffect,
  useRef
} from 'react';

import {
  FnOnDayClick,
  FnOnDayHover,
  NavigationValidationFunctions,
  useCalendarDispatch,
  useCalendarState,
  useHandleCalendarNavigation,
  useModifiers
} from './hooks';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../../elements/Box';
import { formatDate } from '../../DatesRangeSelector/commons';
import dayjs from 'dayjs';
import { DayData } from '../types';
import { mergeDeep } from '../../../../helpers/utils';
import {
  addMonthsNumber,
  getDayData,
  getFirstDayOfMonth,
  getModifiers,
  isDayInRange,
  lastDayOfMonth
} from './helper';
import { weekDaysLong } from '../../../../helpers/localization/localizationUtils';

export type DayItemProps = {
  dayData: DayData;
  setUserInteraction?: (value: boolean) => void;
  onDayClick: FnOnDayClick;
  onDayHover: FnOnDayHover;
  setWasOpenPopup?: (value: boolean) => void;
  enableAvailableMessage?: boolean;
};

const DayItem = (props: DayItemProps) => {
  const {
    dayData,
    onDayHover,
    onDayClick,
    setWasOpenPopup,
    enableAvailableMessage = false,
    ...restProps
  } = props;

  //hooks
  const { state, format, modifiers, numberOfMonths } = useCalendarState();
  const dispatch = useCalendarDispatch();

  // validations functions
  const validations: NavigationValidationFunctions = {
    validateDay: (date: Date) => {
      const dateModifiers = getModifiers(date, modifiers);
      return (
        !dateModifiers.disabled &&
        dateModifiers.interactive &&
        !dateModifiers.blackout &&
        !dateModifiers.hidden
      );
    },
    dataFromDay: (date: Date) => {
      return getDayData(date, state.months);
    },
    isDateOutOfView: (date: Date) => {
      //show data when are off screen
      const endDisplayRange = lastDayOfMonth(
        addMonthsNumber(state.displayDate, numberOfMonths - 1)
      );
      const startDisplayRange = getFirstDayOfMonth(state.displayDate);
      return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
    }
  };

  const navigationHandler = useHandleCalendarNavigation({
    isPopopverOpen: state.popoverOpen,
    validations,
    initialDate: state?.preSelected,
    format,
    component: state.selectedComponent,
    onDayClick,
    dispatch,
    preSelected: state.preSelected,
    selectedRange: state.selectedRange,
    setWasOpenPopup: setWasOpenPopup,
    availableDays: state.availableDays
  });

  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(Context);
  const { translate, localizationData } = context;
  const daysLabels = weekDaysLong(translate, localizationData);
  // const { classes: dayClassNames, styles: modifiersStyles, modifiers: dateModifiers, date: day } =dayData;

  const { classes: dayClassNames, style: modifiersStyles, modifier: dateModifiers } = useModifiers(
    dayData.date
  );
  const day = dayData.date;

  const isPreselectedDay = dateModifiers.preSelected;

  const newPropsModifier = mergeDeep(restProps, modifiersStyles);

  useEffect(() => {
    if (isPreselectedDay && ref.current && state.focusSelectedDay) {
      ref.current.focus();
      if (props.setUserInteraction) {
        props.setUserInteraction(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreselectedDay, state.focusSelectedDay]);

  //events handlers
  const handleSelectDay: MouseEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : (event) => {
        event.preventDefault();
        if (day) {
          onDayClick(dayData);
        }
      };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined = !dateModifiers.interactive
    ? undefined
    : navigationHandler;

  const tabIndex = isPreselectedDay ? 0 : !dateModifiers.interactive ? undefined : -1;

  if (!day) {
    return (
      <Box
        type="td"
        {...newPropsModifier}
        flex={1}
        padding="0"
        className={`calendar-day fr-datepicker-empty ${dayClassNames}`}
        role="cell"
        ariaDisabled
      >
        <Box>&nbsp;</Box>
      </Box>
    );
  }

  const getAriaLabel = () => {
    const formatedDate = formatDate(dayjs(day).toDate(), format, context);
    const dayName = day ? daysLabels[day?.getDay()] : '';
    const prefixArialLabel = dateModifiers.interactive
      ? translate('date-picker-date-available', `Choose`)
      : translate('date-picker-date-not-available', `Not available`);

    return `${enableAvailableMessage ? prefixArialLabel : ''} ${dayName} ${formatedDate}`;
  };

  const focusClass =
    state.focusSelectedDay && state.preSelected && dayjs(day).isSame(state.preSelected, 'day')
      ? 'datepicker-focus-day'
      : '';
  return (
    <Box
      key={`calendar-day-${day?.getDate()} -${day?.getMonth()}`}
      className="calendar-day"
      flex={1}
      padding="0"
      data-testid="calendar-day-item"
      role="cell"
      type="td"
      ariaDisabled={dateModifiers.disabled}
      focusReferences={ref}
      tabIndex={tabIndex}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
      }}
    >
      <Box
        onBlur={() => {
          dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: false });
        }}
        onFocus={() => {
          dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
        }}
        onClick={handleSelectDay}
        onMouseEnter={(event) => {
          onDayHover(event, dayData);
        }}
        ariaLabel={getAriaLabel()}
        aria-disabled={dateModifiers.disabled}
        className={`calendar-day-wrapper ${dayClassNames}`}
      >
        <Box
          type="span"
          className={`calendar-day-inner ${dayClassNames} ${focusClass}`}
          {...newPropsModifier}
          style={{ width: '100%', ...newPropsModifier.style }}
        >
          {day?.getDate()}
        </Box>
      </Box>
    </Box>
  );
};

export default DayItem;
